<template>
  <core-main class="px-8 py-12">
    <!-- Header Section -->
    <template v-slot:toolbar-title>
      {{ $t('form.order.name') }}
    </template>

    <template v-slot:toolbar-icons>
      <v-btn
        slot="activator"
        icon
        @click="setView"
      >
        <v-icon>
          {{ getDisplayIcon }}
        </v-icon>
      </v-btn>

      <v-btn
        slot="activator"
        icon
        @click="toggleFilter"
      >
        <v-icon>
          mdi-filter
        </v-icon>
      </v-btn>
    </template>

    <template #extension>
      <div>
        <v-btn
          dark
          fab
          absolute
          bottom
          small
          color="primary"
          @click="$router.push('/orders/new')"
        >
          <v-icon>
            mdi-plus
          </v-icon>
        </v-btn>
      </div>

      <v-row class="ml-12">
        <v-col
          v-if="!cardView"
          cols="auto"
        >
          <v-select
            v-model="groupBy"
            :items="groupingOptions"
            hide-details
            dense
            solo
            background-color="grey darken-3"
            color="grey darken-3"
          />
        </v-col>

        <v-col>
          <core-search-bar v-model="search" />
        </v-col>
      </v-row>
    </template>

    <core-no-content v-if="_.isEmpty(orders)" />

    <v-row v-else class="fill-height">
      <v-col>
        <v-row v-if="!cardView">
          <v-col>
            <core-main-table
              v-if="groupBy === 'Group by order'"
              :headers="groupedByOrdersHeaders"
              :items="orders"
              :search.sync="search"
              rights-group="rfq"
              show-expand
              @click-row="toggleOrderItemsViewer($event.item)"
            >
              <template v-slot:expanded-item="props">
                <core-order-items-table :items="props.item.orderItems" />
              </template>

              <template v-slot:item.pricing="{ item }">
                {{ formatCurrency(item.pricing) }}
              </template>

              <template v-slot:item.createdAt="{ item }">
                {{ formatDate(item.createdAt) }}
              </template>

              <template v-slot:item.requestedCompletionDate="{ item }">
                {{ formatDate(item.requestedCompletionDate) }}
              </template>

              <template v-slot:item.expectedCompletionDate="{ item }">
                {{ formatDate(item.expectedCompletionDate) }}
              </template>

              <template v-slot:item.shippedDate="{ item }">
                {{ formatDate(item.shippedDate) }}
              </template>

              <template v-slot:item.deliveryDate="{ item }">
                {{ formatDate(item.deliveryDate) }}
              </template>
            </core-main-table>

            <core-main-table
              v-else-if="groupBy === 'Group by none'"
              :headers="groupedByNoneHeaders"
              :items="orderItems"
              :search.sync="search"
              rights-group="rfq"
            >
              <template v-slot:item.partPricing="{ item }">
                <core-table-unit-pricing
                  :item="item.part"
                />
              </template>

              <template v-slot:item.partThickness="{ item }">
                <span>
                  {{ item.part.thickness }} {{ item.part.thickness ? 'mm' : '' }}
                </span>
              </template>

              <template v-slot:item.totalPrice="{ item }">
                <span>
                  {{
                    formatCurrency(getItemPrice(
                      {...item.part, ...{ quantity: item.quantity } })
                      * item.quantity)
                  }}
                </span>
              </template>

              <template v-slot:item.createdAt="{ item }">
                {{ formatDate(item.createdAt) }}
              </template>

              <template v-slot:item.requestedCompletionDate="{ item }">
                {{ formatDate(item.requestedCompletionDate) }}
              </template>

              <template v-slot:item.expectedCompletionDate="{ item }">
                {{ formatDate(item.expectedCompletionDate) }}
              </template>

              <template v-slot:item.shippedDate="{ item }">
                {{ formatDate(item.shippedDate) }}
              </template>

              <template v-slot:item.deliveryDate="{ item }">
                {{ formatDate(item.deliveryDate) }}
              </template>
            </core-main-table>
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col
            v-for="(item, index) of searchedForOrders"
            :key="index"
            md="4"
          >
            <core-rfq-card
              :rfq-data="item"
              :menu-items="orderMenuItems"
              @cancel="confirmCancellation($event)"
              @rfq:click="toggleOrderItemsViewer($event)"
            />
          </v-col>
        </v-row>
      </v-col>

      <v-col
        v-if="areFiltersActive"
        cols="2"
        class="fill-height pt-0"
        style="border-left: 1px solid rgba(0,0,0,.12);"
      >
        <core-filter-main
          @filter:close="areFiltersActive = false"
        >
          <core-filter-checkbox-group
            v-model="filterData.status.value"
            :options="filterData.status.options"
            :section="filterData.status.section"
          />
          <v-divider class="mt-6 ml-4" />

          <core-filter-radio-group
            v-model="filterData.orderBy.value"
            :options="filterData.orderBy.options"
            :section="filterData.orderBy.section"
          />
          <v-divider class="ml-4" />

          <core-filter-range-slider
            v-model="filterData.pricing.value"
            :options="filterData.pricing.options"
            :section="filterData.pricing.section"
          />
          <v-divider class="ml-4" />

          <core-filter-switch-group
            v-model="filterData.ownership.value"
            :options="filterData.ownership.options"
            :section="filterData.ownership.section"
          />
        </core-filter-main>
      </v-col>
    </v-row>

    <v-dialog v-model="cancellationDialog" persistent max-width="400">
      <v-card>
        <v-card-title class="headline">
          Are you sure you want to cancel this order?
        </v-card-title>
        <v-card-text>
          If you continue, a representative will be in contact
          with you shortly to confirm your cancellation.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="red darken-1"
            text
            @click="cancellationDialog = false"
          >
            Disagree
          </v-btn>
          <v-btn
            color="green darken-1"
            text
            @click="cancelOrder"
          >
            Agree
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-navigation-drawer
      v-model="isOrderItemsViewerActive"
      width="50%"
      style="margin-top: 64px"
      fixed
      clipped
      right
      temporary
    >
      <div class="d-flex justify-end mr-2 mt-2">
        <v-btn
          icon
          @click="toggleOrderItemsViewer(false)"
        >
          <v-icon>
            mdi-close
          </v-icon>
        </v-btn>
      </div>

      <v-row justify="center">
        <v-col cols="6">
          <div class="d-flex justify-center">
            <span class="display-1 pb-6">
              Order Summary
            </span>
          </div>

          <div class="d-flex justify-space-between">
            <span class="mr-3 subtitle-1 font-weight-bold">
              PO Number:
            </span>
            <span class="align-end subtitle-1">
              {{ orderItemViewerItems.purchaseOrderNo }}
            </span>
          </div>

          <div class="d-flex justify-space-between">
            <span class="mr-2 subtitle-1 font-weight-bold">
              Order Status:
            </span>
            <span class="align-end subtitle-1">
              {{ orderItemViewerItems.status }}
            </span>
          </div>

          <div class="d-flex justify-space-between">
            <span class="mr-2 subtitle-1 font-weight-bold">
              Number of Parts:
            </span>
            <span class="align-end subtitle-1">
              {{ _.size(orderItemViewerItems.orderItems) }} Parts
            </span>
          </div>

          <div class="d-flex justify-space-between">
            <span class="mr-2 subtitle-1 font-weight-bold">
              Order Price:
            </span>
            <span class="align-end subtitle-1">
              {{ formatCurrency(orderItemViewerItems.pricing) }}
            </span>
          </div>

          <div class="d-flex justify-space-between">
            <span class="mr-2 subtitle-1 font-weight-bold">
              Submitted by:
            </span>
            <span class="align-end subtitle-1">
              {{ orderItemViewerItems.createdBy }}
            </span>
          </div>

          <div class="d-flex justify-space-between">
            <span class="mr-2 subtitle-1 font-weight-bold">
              Ordered on:
            </span>
            <span class="align-end subtitle-1">
              {{ formatDate(orderItemViewerItems.createdAt) }}
            </span>
          </div>

          <div class="d-flex justify-space-between">
            <span class="mr-2 subtitle-1 font-weight-bold">
              Demanded by:
            </span>
            <span class="align-end subtitle-1">
              {{ formatDate(orderItemViewerItems.requestedCompletionDate) }}
            </span>
          </div>

          <div class="d-flex justify-space-between">
            <span class="mr-2 subtitle-1 font-weight-bold">
              Shipped on:
            </span>
            <span class="align-end subtitle-1">
              {{ formatDate(orderItemViewerItems.shippedDate) }}
            </span>
          </div>

          <div class="d-flex justify-space-between">
            <span class="mr-2 subtitle-1 font-weight-bold">
              Delivered on:
            </span>
            <span class="align-end subtitle-1">
              {{ formatDate(orderItemViewerItems.deliveryDate) }}
            </span>
          </div>

          <div class="d-flex justify-space-between">
            <span class="mr-2 subtitle-1 font-weight-bold">
              Delivery speed:
            </span>
            <span class="align-end subtitle-1">
              {{ `${_.get(orderItemViewerItems, 'deliverySpeed.text')} (${_.get(orderItemViewerItems, 'deliverySpeed.businessDays')} days)` }}
            </span>
          </div>

          <div class="d-flex justify-space-between">
            <span class="mr-2 subtitle-1 font-weight-bold">
              Forwarding option:
            </span>
            <span class="align-end subtitle-1">
              {{ orderItemViewerItems.forwardingOption }}
            </span>
          </div>

          <div class="d-flex justify-space-between">
            <span class="mr-2 subtitle-1 font-weight-bold">
              Forwarder:
            </span>
            <span class="align-end subtitle-1">
              {{ orderItemViewerItems.forwarder }}
            </span>
          </div>

          <div class="d-flex justify-space-between">
            <span class="mr-2 subtitle-1 font-weight-bold">
              Billing address:
            </span>
            <span class="align-end subtitle-1">
              {{ formatAddressText(orderItemViewerItems.billingAddress) }}
            </span>
          </div>

          <div class="d-flex justify-space-between">
            <span class="mr-2 subtitle-1 font-weight-bold">
              Shipping address:
            </span>
            <span class="align-end subtitle-1">
              {{ formatAddressText(orderItemViewerItems.shippingAddress) }}
            </span>
          </div>
        </v-col>
      </v-row>

      <v-divider class="ma-4" />

      <v-row class="px-8">
        <v-col>
          <core-order-items-table :items="orderItemViewerItems.orderItems" />
        </v-col>
      </v-row>
    </v-navigation-drawer>
  </core-main>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import UsersMixins from '../../mixins/users';
import CurrencyFormatterMixin from '../../mixins/currencyFormatter';
import FilterableTextMixin from '../mixins/filterableText';
import AddressFormatterMixin from '../../mixins/address-formatter';

export default {

  mixins: [
    UsersMixins,
    CurrencyFormatterMixin,
    FilterableTextMixin,
    AddressFormatterMixin,
  ],

  data() {
    return {
      isOrderItemsViewerActive: false,

      orderItemViewerItems: {},

      search: '',

      areFiltersActive: false,

      cardView: true,

      cancellationDialog: false,

      cancellationId: '',

      groupingOptions: [
        'Group by none',
        'Group by order',
      ],

      groupBy: 'Group by order',

      filterData: {
        status: {
          section: 'Status',
          options: [
            'In Progress',
            'Shipped',
            'Delivered',
          ],
          value: [
          ],
        },
        orderBy: {
          section: 'Order By',
          options: [
            'Ordered by',
            'Delivery Date',
            'Status',
            'Number of Parts'
          ],
          value: '',
        },
        pricing: {
          section: 'Pricing',
          min: 0,
          max: 999999,
          value: [
            0, 0
          ],
        },
        ownership: {
          section: 'Ownership',
          options: [
            'Submitted by Me',
          ],
          value: [
          ],
        },
      },
    };
  },

  computed: {
    ...mapGetters('orders', { findFromStore: 'find' }),

    orders() {
      return this.findFromStore({ query: {} }).data;
    },

    searchedForOrders() {
      return this.filterItems(this.orders, this.search);
    },

    orderItems() {
      const items = [
      ];

      _.forEach(this.orders, order => {
        _.forEach(order.orderItems, i => {
          items.push(_.omit({ ...order, ...i }, 'orderItems'));
        });
      });

      return items;
    },

    groupedByNoneHeaders() {
      return [
        {
          text: 'Purchase order',
          value: 'purchaseOrderNo',
        },
        {
          text: 'Sales order',
          value: 'salesOrderNo',
        },
        {
          text: 'Submitted by',
          value: 'createdBy',
        },
        {
          text: 'Client part #',
          value: 'part.clientPartNo',
        },
        {
          text: 'Komacut Part #',
          value: 'part.manufacturerPartNo',
        },
        {
          text: 'Description',
          value: 'part.description',
        },
        {
          text: 'Material',
          value: 'part.material',
        },
        {
          text: 'Finish',
          value: 'part.finish',
        },
        {
          text: 'Thickness',
          value: 'part.thickness',
        },
        {
          text: 'Unit price',
          value: 'part.pricing',
        },
        {
          text: 'Qty',
          value: 'quantity',
        },
        {
          text: 'Total price',
          value: 'totalPrice',
        },
        {
          text: 'Status',
          value: 'status',
        },
        {
          text: 'Ordered on',
          value: 'createdAt',
        },
        {
          text: 'Demanded by',
          value: 'requestedCompletionDate',
        },
        {
          text: 'Expected by',
          value: 'expectedCompletionDate',
        },
        {
          text: 'Shipped on',
          value: 'shippedDate',
        },
        {
          text: 'Delivered on',
          value: 'deliveryDate',
        },
      ];
    },

    orderMenuItems() {
      return [
        { label: 'Re-order' },
        { label: 'View Shipping docs' },
        { label: 'Download PO receipt' },
        { label: 'Cancel order' },
      ];
    },

    groupedByOrdersHeaders() {
      return [
        {
          text: '',
          value: 'data-table-expand',
        },
        {
          text: 'Purchase order',
          value: 'purchaseOrderNo',
        },
        {
          text: 'Sales order',
          value: 'salesOrderNo',
        },
        {
          text: 'Submitted by',
          value: 'createdBy',
        },
        {
          text: 'Amount',
          value: 'pricing',
        },
        {
          text: 'Status',
          value: 'status',
        },
        {
          text: 'Ordered on',
          value: 'createdAt',
        },
        {
          text: 'Demanded by',
          value: 'requestedCompletionDate',
        },
        {
          text: 'Expected by',
          value: 'expectedCompletionDate',
        },
        {
          text: 'Shipped on',
          value: 'shippedDate',
        },
        {
          text: 'Delivered on',
          value: 'deliveryDate',
        },
        {
          text: '',
          value: 'data-table-menu',
          items: this.orderMenuItems,
        },
      ];
    },

    orderItemsTableHeaders() {
      return [
        {
          text: 'Client part #',
          value: 'part.clientPartNo',
        },
        {
          text: 'Komacut Part #',
          value: 'part.manufacturerPartNo',
        },
        {
          text: 'Description',
          value: 'part.description',
        },
        {
          text: 'Material',
          value: 'part.material',
        },
        {
          text: 'Finish',
          value: 'part.finish',
        },
        {
          text: 'Thickness',
          value: 'part.thickness',
        },
        {
          text: 'Unit price',
          value: 'part.pricing',
        },
        {
          text: 'Qty',
          value: 'quantity',
        },
        {
          text: 'Total price',
          value: 'totalPrice',
        }
      ];
    },

    getDisplayIcon() {
      return this.cardView ? 'mdi-format-list-bulleted' : 'mdi-view-grid';
    },

    getPartName() {
      return `/img/placeholder_item_${Math.floor(Math.random() * 3) + 1}.png`;
    },
  },

  mounted() {
    this.findFromBackend({ query: {} });
  },

  methods: {
    ...mapActions('orders', { findFromBackend: 'find', removeOrder: 'remove' }),

    confirmCancellation(orderId) {
      this.cancellationId = orderId;
      this.cancellationDialog = true;
    },

    async cancelOrder() {
      const order = _.find(this.orders, o => o._id === this.cancellationId);
      if (!order) {
        return;
      }

      order.status = 'On Hold';
      await order.save();
      this.cancellationId = '';
      this.cancellationDialog = false;
    },

    setView() {
      this.cardView = !this.cardView;
    },

    toggleFilter() {
      this.areFiltersActive = !this.areFiltersActive;
    },

    formatDate(date) {
      if (!moment(date).isValid()) {
        return '~';
      }
      return moment(date).format('YYYY-MM-DD');
    },

    getItemPrice(item) {
      const sortedByPrice = _.sortBy(item.pricing, p => p.minQuantity);
      const itemPrice = _.find(sortedByPrice, p => p.minQuantity >= item.quantity);
      if (itemPrice) {
        return itemPrice.price;
      }
      return item.quantity ? _.last(sortedByPrice).price : 0;
    },

    toggleOrderItemsViewer(orderItems) {
      this.isOrderItemsViewerActive = !!orderItems;
      this.orderItemViewerItems = orderItems;
    },
  },
};
</script>
